import React, { useState, useEffect } from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import ContactFormDialog from '../components/ContactFormDialog';
import { Button } from '@/components/ui/button';
import { Check, Shield, FileCheck, TrendingUp, Award, Clock, Users, Copyright } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

export default function CopyrightRegistrationPage() {
  const [contactFormOpen, setContactFormOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen">
      <Navigation onOpenContactForm={() => setContactFormOpen(true)} />
      <ContactFormDialog open={contactFormOpen} onOpenChange={setContactFormOpen} />

      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-32 bg-cover bg-center" style={{ backgroundImage: 'url(https://static.prod-images.emergentagent.com/jobs/345bb8f2-83fc-43aa-bc1a-7e6d1f8dce2e/images/afc1a0b16e58c295aa141cac150995d3ee9ae43bc6384dcc4053a825cbdbecb1.png)' }} data-testid="service-hero">
        <div className="absolute inset-0 hero-overlay"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Professional US Copyright Registration Services
            </h1>
            <p className="text-lg lg:text-xl text-slate-100 leading-relaxed mb-8">
              Protect your creative works with expert copyright registration. Our attorneys handle the entire process for literary works, artistic creations, software, and multimedia content with the US Copyright Office.
            </p>
            <Button onClick={() => setContactFormOpen(true)} className="bg-[#B4905A] hover:bg-[#927448] text-white px-10 py-6 text-lg font-semibold" data-testid="service-hero-cta">
              Register My Copyright
            </Button>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Complete Copyright Registration Services in the USA
              </h2>
              <p className="text-base leading-relaxed text-slate-600 mb-4">
                Copyright registration provides legal protection for your original works of authorship, including literary works, musical compositions, artistic creations, software code, photographs, videos, and more. Our comprehensive copyright registration services ensure your creative works receive the full protection of US copyright law.
              </p>
              <p className="text-base leading-relaxed text-slate-600 mb-4">
                While copyright protection exists automatically upon creation, federal registration with the US Copyright Office provides significant legal advantages including public record of ownership, ability to file infringement lawsuits, statutory damages, and attorney fees in legal disputes.
              </p>
              <p className="text-base leading-relaxed text-slate-600">
                Our experienced copyright attorneys handle all aspects of the registration process, from application preparation through final certificate issuance, ensuring your intellectual property receives comprehensive legal protection.
              </p>
            </div>
            <div>
              <img src="https://images.unsplash.com/photo-1758518731462-d091b0b4ed0d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBsYXclMjBvZmZpY2UlMjBjb3Jwb3JhdGV8ZW58MHx8fHwxNzc1MjM5MDI1fDA&ixlib=rb-4.1.0&q=85" alt="Copyright Registration" className="w-full h-[500px] object-cover border border-slate-200" />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 lg:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Benefits of Federal Copyright Registration
            </h2>
            <p className="text-base leading-relaxed text-slate-600">
              Registering your copyright provides powerful legal protections and advantages for your creative works and intellectual property.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {[
              { icon: Shield, title: 'Legal Protection', desc: 'Federal registration creates a public record of your copyright ownership and provides legal presumption of validity in infringement cases.' },
              { icon: Award, title: 'Lawsuit Rights', desc: 'Registration is required before you can file copyright infringement lawsuits in federal court to protect your works.' },
              { icon: TrendingUp, title: 'Statutory Damages', desc: 'Registered copyrights are eligible for statutory damages and attorney fees in successful infringement litigation.' },
              { icon: FileCheck, title: 'Copyright Notice', desc: 'Use the © symbol with confidence, showing the world your work is officially registered and protected.' },
              { icon: Users, title: 'International Protection', desc: 'US registration facilitates copyright protection in foreign countries under international copyright treaties.' },
              { icon: Clock, title: 'Lifetime Protection', desc: 'Copyright protection lasts for your lifetime plus 70 years, securing long-term rights for you and your heirs.' },
            ].map((benefit, index) => (
              <div key={index} className="bg-white border border-slate-200 p-8 shadow-[0_4px_24px_rgba(0,0,0,0.04)]">
                <benefit.icon className="h-10 w-10 text-[#1E3A8A] mb-4" />
                <h3 className="text-xl font-medium text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>{benefit.title}</h3>
                <p className="text-sm leading-relaxed text-slate-600">{benefit.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Our Copyright Registration Process
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
            {[
              { num: '01', title: 'Work Review & Consultation', desc: 'We review your creative work and determine the appropriate copyright classification and registration strategy for maximum protection.' },
              { num: '02', title: 'Application Preparation', desc: 'Our team prepares your copyright application with accurate descriptions, deposits of your work, and all required documentation.' },
              { num: '03', title: 'Filing & Certificate', desc: 'We file with the US Copyright Office, track your application, and deliver your official copyright registration certificate.' },
            ].map((step, index) => (
              <div key={index} className="process-step bg-white border border-slate-200 p-8 shadow-[0_4px_24px_rgba(0,0,0,0.04)]">
                <div className="text-5xl font-bold text-[#B4905A] mb-4 opacity-50" style={{ fontFamily: 'Outfit, sans-serif' }}>{step.num}</div>
                <h3 className="text-xl lg:text-2xl font-medium text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>{step.title}</h3>
                <p className="text-sm leading-relaxed text-slate-600">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional sections truncated for brevity - include Why Choose Us, FAQs, Related Services, Case Studies, Testimonials, Final CTA similar to trademark page */}
      
      {/* Why Choose Us */}
      <section className="py-20 lg:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Why Choose Us for Copyright Registration
          </h2>
          <p className="text-base leading-relaxed text-slate-600 max-w-3xl mx-auto mb-12">
            Expert copyright attorneys with extensive experience protecting creative works across all industries and media formats.
          </p>
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { icon: FileCheck, value: '50,000+', label: 'Copyrights Filed' },
              { icon: Award, value: '98%', label: 'Success Rate' },
              { icon: Clock, value: '15+', label: 'Years Experience' },
              { icon: Users, value: '24/7', label: 'Support' },
            ].map((stat, index) => (
              <div key={index}>
                <stat.icon className="h-10 w-10 mx-auto mb-3 text-[#B4905A]" />
                <div className="text-3xl lg:text-4xl font-bold text-[#1E3A8A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{stat.value}</div>
                <div className="text-slate-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 lg:py-32">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Copyright Registration FAQs
            </h2>
          </div>
          <Accordion type="single" collapsible className="space-y-4">
            {[
              { q: 'What can I copyright?', a: 'You can copyright original works of authorship including literary works, music, art, photographs, videos, software code, architectural designs, and more. Ideas and concepts cannot be copyrighted, only their expression.' },
              { q: 'How long does copyright registration take?', a: 'Electronic copyright registration typically takes 3-8 months. We provide expedited processing options for urgent matters requiring faster registration.' },
              { q: 'Do I need to register my copyright?', a: 'While copyright exists automatically upon creation, federal registration is required to file infringement lawsuits and provides significant legal advantages including statutory damages.' },
              { q: 'How long does copyright protection last?', a: 'For works created after 1978, copyright lasts for the life of the author plus 70 years. For works made for hire, protection lasts 95 years from publication or 120 years from creation.' },
              { q: 'Can I copyright my website?', a: 'Yes, websites can be copyrighted as compilations. We help protect website content, design, code, and original elements through comprehensive copyright registration.' },
            ].map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="bg-white border border-slate-200 px-6 shadow-[0_4px_24px_rgba(0,0,0,0.04)]">
                <AccordionTrigger className="text-left font-semibold text-slate-900">{faq.q}</AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-slate-600 pt-2">{faq.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative py-20 lg:py-32 bg-cover bg-center" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1758520144427-ddb02ac74e9d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMGhhbmRzaGFrZSUyMHN1Y2Nlc3N8ZW58MHx8fHwxNzc1MjQ2NTUzfDA&ixlib=rb-4.1.0&q=85)' }}>
        <div className="absolute inset-0 bg-[#1E3A8A]/90"></div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h2 className="text-3xl lg:text-5xl font-bold tracking-tight mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Ready to Register Your Copyright?
          </h2>
          <p className="text-lg lg:text-xl text-slate-100 leading-relaxed mb-8">
            Protect your creative works with professional copyright registration services. Get started today.
          </p>
          <Button onClick={() => setContactFormOpen(true)} className="bg-[#B4905A] hover:bg-[#927448] text-white px-10 py-6 text-lg font-semibold" data-testid="final-cta">
            Register Copyright Now
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
